import { useCallback } from "react";
import { FieldEditability } from "./profileFormMapper";
import { FormData } from "./typesIN";

/**
 * Returns a function `isLocked(fieldKey)` that implements:
 *   - If field is in `alwaysReadOnly` list → locked regardless
 *   - If field is in `alwaysEditable` list → never locked
 *   - If fieldEditability[fieldKey] === true AND formData has a value → locked
 *   - If fieldEditability[fieldKey] === true BUT formData is empty → editable (fallback)
 *   - Default: editable
 */
export const useFieldLock = (
  formData: FormData,
  editability: FieldEditability = {},
  alwaysReadOnly: string[] = [],
  alwaysEditable: string[] = [],
) => {
  const isLocked = useCallback(
    (fieldKey: string): boolean => {
      // Always read-only (Excel spec: employer signature, certain dates, etc.)
      if (alwaysReadOnly.includes(fieldKey)) return true;
      // Always editable regardless of profile
      if (alwaysEditable.includes(fieldKey)) return false;
      // Profile has data for this field AND form has a value → lock it
      if (editability[fieldKey] === true) {
        const val = formData[fieldKey];
        // Has actual data → lock
        if (val !== undefined && val !== null && val !== "") return true;
        // Profile said it has data but form value is empty → allow edit (fallback)
        return false;
      }
      // Not in editability map → editable by default
      return false;
    },
    [formData, editability, alwaysReadOnly, alwaysEditable],
  );

  return isLocked;
};
